import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import Modal from '../components/Modal';
import LegalDocumentView from '../components/LegalDocumentView';
import { LegalDocumentContent, LegalDocumentKey, LegalSectionContent } from '../types';

const DOCUMENTS: { key: LegalDocumentKey; label: string; publicPath: string }[] = [
  { key: 'privacy', label: 'Data Privacy Policy', publicPath: '/privacy' },
  { key: 'terms', label: 'Terms and Conditions', publicPath: '/terms' }
];

/**
 * The User Agreement — the Super Admin's home for both documents an
 * applicant agrees to: the Data Privacy Policy and the Terms and
 * Conditions.
 *
 * Together they are the agreement; separately they are two pages, which
 * is why they share one screen rather than sitting under two sidebar
 * entries. Each is still published on its own public path, because that
 * is where they are linked from and how they are read.
 *
 * The privacy text is not only a page: it is reproduced inside the
 * consent box on the registration form, which applicants must tick
 * before an account is created. Editing it changes what people are
 * agreeing to, which is why this page previews the result and says so
 * rather than presenting the documents as ordinary site copy.
 */
const UserAgreementPage = () => {
  const [doc, setDoc] = useState<LegalDocumentKey>('privacy');
  const [content, setContent] = useState<LegalDocumentContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(false);
  // Same reasoning as the home page editor: Save sits below a long form,
  // so a banner at the top of the page would land off-screen.
  const [showSavedModal, setShowSavedModal] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    apiService
      .getLegalDocument(doc)
      .then((loaded) => {
        if (active) setContent(loaded);
      })
      .catch((err: any) => {
        if (active) setError(err.message || 'Failed to load this document');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [doc]);

  const patch = (changes: Partial<LegalDocumentContent>) =>
    setContent((prev) => (prev ? { ...prev, ...changes } : prev));

  const patchSection = (index: number, changes: Partial<LegalSectionContent>) =>
    setContent((prev) =>
      prev
        ? { ...prev, sections: prev.sections.map((s, i) => (i === index ? { ...s, ...changes } : s)) }
        : prev
    );

  const removeSection = (index: number) =>
    setContent((prev) => (prev ? { ...prev, sections: prev.sections.filter((_, i) => i !== index) } : prev));

  const moveSection = (index: number, delta: number) =>
    setContent((prev) => {
      if (!prev) return prev;
      const target = index + delta;
      if (target < 0 || target >= prev.sections.length) return prev;
      const sections = [...prev.sections];
      [sections[index], sections[target]] = [sections[target], sections[index]];
      return { ...prev, sections };
    });

  const addSection = () =>
    setContent((prev) =>
      prev ? { ...prev, sections: [...prev.sections, { heading: '', body: '' }] } : prev
    );

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content) return;
    setError('');
    setSaving(true);
    try {
      // Re-render from what the server stored: it strips anything
      // outside the document's shape, so this is what the public page
      // and the registration consent box will actually show.
      setContent(await apiService.updateLegalDocument(doc, content));
      setShowSavedModal(true);
    } catch (err: any) {
      setError(err.message || 'Failed to save this document');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Pulls the shipped text back from the server rather than from a
   * bundled copy, so there is only ever one version of the original.
   * Does not save — the Super Admin still has to look at it and press
   * Save, since this discards whatever they had written.
   */
  const handleRestoreOriginal = async () => {
    setError('');
    try {
      setContent(await apiService.getLegalDocumentDefault(doc));
    } catch (err: any) {
      setError(err.message || 'Failed to load the original text');
    }
  };

  const active = DOCUMENTS.find((d) => d.key === doc)!;

  return (
    <div className="container">
      <div className="page-header">
        <h1>User Agreement</h1>
        <p>
          What every applicant agrees to: the Data Privacy Policy and the Terms and Conditions. Changes go live
          as soon as you save.
        </p>
      </div>

      {/* Stated because it is the thing most easily missed here: this is
          not only page copy. The privacy text is reproduced inside the
          consent box on the registration form. */}
      <div className="alert-error" style={{ padding: '1rem', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        The Data Privacy Policy is shown in full on the registration form, where applicants must tick to agree
        before an account is created. Editing it changes what they are agreeing to.
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {DOCUMENTS.map((d) => (
          <button
            key={d.key}
            type="button"
            className={d.key === doc ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
            onClick={() => setDoc(d.key)}
          >
            {d.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="alert-error" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
          {error}
        </div>
      )}

      {loading || !content ? (
        <p>Loading...</p>
      ) : (
        <form onSubmit={handleSave}>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header">
              <h3>Document</h3>
            </div>
            <div className="form-group">
              <label>Title</label>
              <input value={content.title} onChange={(e) => patch({ title: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Last updated</label>
              <input
                value={content.lastUpdated}
                onChange={(e) => patch({ lastUpdated: e.target.value })}
                placeholder="23 September 2026"
              />
              <small style={{ color: 'var(--text-secondary)' }}>
                Shown to readers. Set it to the date the wording last meaningfully changed.
              </small>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={content.draft}
                  onChange={(e) => patch({ draft: e.target.checked })}
                  style={{ marginTop: '0.2rem' }}
                />
                <span>
                  Mark as a draft
                  <br />
                  <small style={{ color: 'var(--text-secondary)' }}>
                    Shows a "not yet in force" banner to readers. Leave this on until the text has been
                    reviewed and every "TO BE CONFIRMED BY THE PROGRAMME" note has been replaced.
                  </small>
                </span>
              </label>
            </div>
          </div>

          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header">
              <h3>Sections</h3>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Plain text only. Leave a blank line between paragraphs, and start a line with{' '}
              <code>- </code> to make it a bullet. Formatting tags are not accepted and will be shown as
              written.
            </p>

            {content.sections.map((section, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid var(--border-light)',
                  borderRadius: 'var(--radius-md)',
                  padding: '1rem',
                  marginBottom: '0.75rem'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem'
                  }}
                >
                  <strong style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    Section {index + 1}
                  </strong>
                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                    <button
                      className="btn btn-outline btn-sm"
                      type="button"
                      onClick={() => moveSection(index, -1)}
                      disabled={index === 0}
                      aria-label={`Move section ${index + 1} up`}
                    >
                      ↑
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      type="button"
                      onClick={() => moveSection(index, 1)}
                      disabled={index === content.sections.length - 1}
                      aria-label={`Move section ${index + 1} down`}
                    >
                      ↓
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      type="button"
                      onClick={() => removeSection(index)}
                      aria-label={`Remove section ${index + 1}`}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Heading</label>
                  <input
                    value={section.heading}
                    onChange={(e) => patchSection(index, { heading: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Text</label>
                  <textarea
                    value={section.body}
                    onChange={(e) => patchSection(index, { body: e.target.value })}
                    rows={8}
                  />
                </div>
              </div>
            ))}

            <button className="btn btn-outline btn-sm" type="button" onClick={addSection}>
              Add a section
            </button>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button className="btn btn-outline" type="button" onClick={() => setPreview(true)}>
              Preview
            </button>
            <button className="btn btn-outline" type="button" onClick={handleRestoreOriginal}>
              Restore the original text
            </button>
            <Link className="btn btn-outline" to={active.publicPath} target="_blank" rel="noopener noreferrer">
              View the live page
            </Link>
          </div>
        </form>
      )}

      {preview && content && (
        <Modal title={`Preview — ${active.label}`} onClose={() => setPreview(false)}>
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <LegalDocumentView content={content} />
          </div>
        </Modal>
      )}

      {showSavedModal && (
        <Modal title="Saved" onClose={() => setShowSavedModal(false)}>
          <p>{active.label} has been updated and is now live.</p>
        </Modal>
      )}
    </div>
  );
};

export default UserAgreementPage;
